/**
 * An Auth0 session cannot ask for a token that names this context: the session knows no Auth0 organization for
 * it (its organization has no identity-provider reference). Fail closed: the context cannot be opened here.
 */
export class ContextNotCarriedError extends Error {
    name = 'ContextNotCarriedError';
    contextId;
    constructor(contextId) {
        super('This session cannot get a token for that context: its organization has no Auth0 organization.');
        this.contextId = contextId;
    }
}
/**
 * The session left (or is leaving) for the identity provider to sign the person in to an organization, because
 * no silent token was in it. Nothing failed: the page is navigating, and the sign-in comes back to
 * /auth/callback with the context in its app state.
 */
export class SignInRedirectError extends Error {
    name = 'SignInRedirectError';
    organization;
    constructor(organization) {
        super('Signing in to the organization through the identity provider; the page is leaving.');
        this.organization = organization;
    }
}
export function isSignInRedirectError(value) {
    return value instanceof SignInRedirectError;
}
export function isContextNotCarriedError(value) {
    return value instanceof ContextNotCarriedError;
}
/** The session ended (expired, revoked or never existed). Show the session-expired state once; never loop. */
export class SessionExpiredError extends Error {
    name = 'SessionExpiredError';
    /** The provider or API error code that ended the session, for example login_required. */
    reason;
    constructor(reason, options) {
        super(`The Trace session has ended (${reason}).`, options);
        this.reason = reason;
    }
}
export function isSessionExpiredError(value) {
    return value instanceof SessionExpiredError;
}
/**
 * Completing a sign-in failed. `appState` is what the hub needs to offer the right way back (for example
 * the product of the login page). The provider's error_description is deliberately not kept: it arrives
 * through the URL, so anyone can craft it, and it must never be rendered.
 */
export class SignInError extends Error {
    name = 'SignInError';
    code;
    appState;
    /** The OAuth error code from the provider (for example access_denied), when there was one. */
    providerError;
    constructor(code, appState, providerError, options) {
        super(`Sign-in could not be completed (${code}).`, options);
        this.code = code;
        this.appState = appState;
        this.providerError = providerError;
    }
}
export function isSignInError(value) {
    return value instanceof SignInError;
}
//# sourceMappingURL=types.js.map